import type { RefundRequestDetail, RefundRequestStatusTab } from '@/@types/RefundRequest';
import { usePermissions } from '@/auth/PermissionsContext';
import { RefundRequestsService } from '@/services/refund-requests.service';
import useCurrencyStore from '@/store/useCurrencyStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useDebounce } from 'use-debounce';

const QUERY_CONFIG = {
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60 * 2,
} as const;

export const REFUND_REQUESTS_QUERY_KEY = 'refund-requests';
export const REFUND_REQUESTS_SUMMARY_QUERY_KEY = 'refund-requests-summary';
export const REFUND_REQUEST_DETAIL_QUERY_KEY = 'refund-request-detail';

export const REFUND_REQUEST_STATUS_TABS: RefundRequestStatusTab[] = ['all', 'pending', 'mediation', 'refunding', 'closed'];

const normalizeTab = (value: string): RefundRequestStatusTab =>
  REFUND_REQUEST_STATUS_TABS.includes(value as RefundRequestStatusTab)
    ? (value as RefundRequestStatusTab)
    : 'all';

export function useRefundRequestsSummary() {
  const currency = useCurrencyStore((s) => s.currency);
  const { hasPermission, isPrimary } = usePermissions();

  return useQuery({
    queryKey: [REFUND_REQUESTS_SUMMARY_QUERY_KEY, currency],
    queryFn: () => RefundRequestsService.getSummary(currency),
    enabled: isPrimary || hasPermission('disputes.view_disputes'),
    ...QUERY_CONFIG,
  });
}

export function useRefundRequestDetail(id: string | null) {
  return useQuery({
    queryKey: [REFUND_REQUEST_DETAIL_QUERY_KEY, id],
    queryFn: () => RefundRequestsService.show(id!),
    enabled: Boolean(id),
    ...QUERY_CONFIG,
  });
}

function useRefreshAfterAction() {
  const queryClient = useQueryClient();

  return (detail: RefundRequestDetail) => {
    queryClient.setQueryData([REFUND_REQUEST_DETAIL_QUERY_KEY, detail.id], detail);
    queryClient.invalidateQueries({ queryKey: [REFUND_REQUESTS_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [REFUND_REQUESTS_SUMMARY_QUERY_KEY] });
  };
}

export function useReloadAfterRefusal() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [REFUND_REQUEST_DETAIL_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [REFUND_REQUESTS_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [REFUND_REQUESTS_SUMMARY_QUERY_KEY] });
  };
}

export function useStartRefundMediation() {
  const refresh = useRefreshAfterAction();
  const reload = useReloadAfterRefusal();

  return useMutation({
    mutationFn: (id: string) => RefundRequestsService.startMediation(id),
    onSuccess: refresh,
    onError: reload,
  });
}

export function useSendRefundMessage() {
  const refresh = useRefreshAfterAction();
  const reload = useReloadAfterRefusal();

  return useMutation({
    mutationFn: ({ id, body, attachment }: { id: string; body: string; attachment?: File | null }) =>
      RefundRequestsService.sendMessage(id, body, attachment),
    onSuccess: refresh,
    onError: reload,
  });
}

export function useRefundRequests() {
  const [currentPage, setCurrentPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [searchQuery, setSearchQuery] = useQueryState('q', parseAsString.withDefault(''));
  const [statusTab, setStatusTabState] = useQueryState('status', parseAsString.withDefault('all'));
  const [selectedId, setSelectedId] = useQueryState('request', parseAsString);
  const [debouncedSearch] = useDebounce(searchQuery, 400);

  const activeTab = normalizeTab(statusTab);

  const { data, isLoading } = useQuery({
    queryKey: [REFUND_REQUESTS_QUERY_KEY, activeTab, debouncedSearch, currentPage],
    queryFn: () =>
      RefundRequestsService.list({ status: activeTab, q: debouncedSearch, page: currentPage }),
    ...QUERY_CONFIG,
  });

  const setStatusTab = (tab: RefundRequestStatusTab) => {
    setStatusTabState(tab);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value || null);
    setCurrentPage(1);
  };

  return {
    requests: data?.data ?? [],
    tableData: { data: data?.data ?? [], meta: data?.meta, links: data?.links },
    total: data?.meta?.total ?? 0,
    isLoading,
    currentPage,
    setCurrentPage,
    searchQuery,
    handleSearchChange,
    activeTab,
    setStatusTab,
    selectedId,
    openRequest: (id: string) => setSelectedId(id),
    closeRequest: () => setSelectedId(null),
  };
}
